import React, { useEffect, useState } from 'react';

// API/Components
import { AnalysisResultToStateColor, isEmpty, GetBVSModel, formatDateTime, isValidValue, measUnitsConversion, userMetricUnits } from './enums';

// Material UI
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';

// Table display if empty
const StyledGridOverlay = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	'& .ant-empty-img-1': {
		fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
	},
	'& .ant-empty-img-2': {
		fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
	},
	'& .ant-empty-img-3': {
		fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
	},
	'& .ant-empty-img-4': {
		fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
	},
	'& .ant-empty-img-5': {
		fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
		fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
	},
}));

function NoDataOverlay() {
	return (
		<StyledGridOverlay>
			<svg
				width="120"
				height="100"
				viewBox="0 0 184 152"
				aria-hidden
				focusable="false"
			>
				<g fill="none" fillRule="evenodd">
					<g transform="translate(24 31.67)">
						<ellipse
							className="ant-empty-img-5"
							cx="67.797"
							cy="106.89"
							rx="67.797"
							ry="12.668"
						/>
						<path
							className="ant-empty-img-1"
							d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
						/>
						<path
							className="ant-empty-img-2"
							d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
						/>
						<path
							className="ant-empty-img-3"
							d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
						/>
					</g>
					<path
						className="ant-empty-img-3"
						d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
					/>
					<g className="ant-empty-img-4" transform="translate(149.65 15.383)">
						<ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
						<path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
					</g>
				</g>
			</svg>
			<Box sx={{ mt: 1 }}>No System Discharge Trend Data</Box>
		</StyledGridOverlay>
	);
}

// Determine if the user is using metric or imperial units
const tempMeasurementUnits = userMetricUnits() ? '°C' : '°F';

// Voltage, Current, and Temperature cell colorization
function buildColorizedCell(measurement, measUnits, measRounding, emptyText, outUnits, outRounding, isTempMeas=false) {
	// Format measurement text
	const measDecimals = measRounding? measRounding : 3;
	const value = isTempMeas ? measUnitsConversion(measurement.measurement) : measurement.measurement;
	const measValue = (isValidValue(value) && measurement.valid) ? value.toFixed(measDecimals) : emptyText;
	const measText = `${measValue} ${measUnits}`;

	// Format value out text
	const outDecimals = outRounding? outRounding : 3;
	const out = isTempMeas ? measUnitsConversion(measurement.out, true) : measurement.out;
	const outValue = (out && isValidValue(out) && out !== 0) ? out.toFixed(outDecimals) : '';
	const outSign = (out && out !== 0 && out > 0) ? '+' : '';
	const outText = (out && isValidValue(out) && out !== 0) ? ` (${outSign}${outValue} ${outUnits})` : '';
	
	const statusColor = AnalysisResultToStateColor(measurement.result);
	
	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				backgroundColor: statusColor + "aa",
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Typography
				fontFamily='Saira, sans-serif'
			>
				{measText}{outText}
			</Typography>
		</div>
	);
}

function buildCell(value, alignment) {
	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: alignment,
				justifyContent: alignment,
			}}
		>
			<Typography fontFamily='Saira, sans-serif'>{value}</Typography>
		</div>
	);
}

// System Trend Report table columns
const columns = [
	{
		field: 'datetime',
		headerName: 'Measurement',
		description: 'Measurement Date and Time',
		minWidth: 170,
		flex: 1,
		headerAlign: "left",
		renderCell: (params) => formatDateTime(params.value),
	},
	{
		field: 'time_elapsed',
		headerName: 'Time Elapsed',
		description: 'Time Elapsed',
		minWidth: 120,
		flex: 0.5,
		headerAlign: "center",
		renderCell: (params) => buildCell(params.value, 'center'),
	},
	{
		field: 'voltage',
		headerName: 'System Voltage',
		description: 'System Voltage',
		minWidth: 210,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildColorizedCell(params.value, 'VDC', 1, '---.-', 'VDC', 2),
	},
	{
		field: 'current',
		headerName: 'System Current',
		description: 'System Current',
		minWidth: 190,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildColorizedCell(params.value, 'A', 1, '--.-', 'A', 1)
	},
	{
		field: 'power',
		headerName: 'Calculated Power',
		description: 'Calculated System Power',
		minWidth: 190,
		flex: 0.5,
		headerAlign: "center",
		renderCell: (params) => buildColorizedCell(params.value, 'KW', 1, '----.-', '', 1)
	},
	{
		field: 'temperature',
		headerName: 'Ambient Temp',
		description: 'Ambient Temperature',
		minWidth: 190,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildColorizedCell(params.value, tempMeasurementUnits, 1, '--.-', tempMeasurementUnits, 1, true)
	},
	// {
	// 	field: 'model',
	// 	headerName: 'Model',
	// 	description: 'BVM Model',
	// 	minWidth: 90,
	// 	flex: 0.5,
	// 	headerAlign: "center",
	// 	renderCell: (params) => buildCell(GetBVSModel(params.value), 'center'),
	// },
];


export default function SystemDischargeTrendReport(props) {
	const { trendData, loading } = props;
	
	const [reportTrendData, setReportTrendData] = useState([]);
	const [isLoading, setIsLoading] = useState(loading);
	const [pageSize, setPageSize] = useState(100);
	
	useEffect(() => {
		const formattedData = FormatDataForReport(trendData);
		if (formattedData !== undefined && formattedData !== null) {
			setReportTrendData(formattedData);
		}
	}, [trendData]);

	useEffect(() => {
		setIsLoading(loading);
	}, [loading]);
	
	function FormatDataForReport(trendData) {
		setIsLoading(true);
		let rows = [];
		if (trendData !== null && trendData !== undefined && !isEmpty(trendData)) {
			trendData.measurements?.forEach(meas => {
				rows.push({
					datetime: meas.datetime,
					time_elapsed: meas.time_elapsed,
					voltage: {
						measurement: meas.voltage.measurement,
						result: meas.voltage.analysis_result,
						out: meas.voltage.analysis_out,
						valid: meas.voltage.analysis_valid,
					},
					current: {
						measurement: meas.current.measurement,
						result: meas.current.analysis_result,
						out: meas.current.analysis_out,
						valid: meas.current.analysis_valid,
					},
					power: {
						measurement: meas.power.measurement,
						result: meas.power.analysis_result,
						out: meas.power.analysis_out,
						valid: meas.power.analysis_valid,
					},
					temperature: {
						measurement: meas.temperature.measurement,
						result: meas.temperature.analysis_result,
						out: meas.temperature.analysis_out,
						valid: meas.temperature.analysis_valid,
					},
					model: meas.bvs_model,
				});
			});
		}
		
		setIsLoading(false);
		return rows;
	}
	  
	return (
		<DataGrid
			getRowId={(row) => `${row.datetime}`}
			rows={reportTrendData}
			columns={columns}
			loading={isLoading}
			density="compact"
			pageSize={pageSize}
			onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
			rowsPerPageOptions={[25, 50, 100]}  // Max pagination size for DataGrid (Free Edition) is 100
			components={{
				NoRowsOverlay: NoDataOverlay,
			}}
			sx={{
				padding: 1,
				height: '100%'
			}}
		/>
	);
}